/**
 * Track Builder configuration.
 * Place track tiles, route through stations, reach the end building.
 */

export const PIPELINE_CONFIG = {
    TARGET_CELL_SIZE: 70,
    PADDING_TOP: 100,
    PADDING_SIDE: 20,
    PADDING_BOTTOM: 20,

    COUNTDOWN_SECONDS: 20,

    TRAIN_BASE_SPEED: 0.35,
    TRAIN_SPEED_INCREMENT: 0.012,
    TRAIN_MAX_SPEED: 2.0,

    POINTS_PER_TILE: 10,
    POINTS_CROSS_BONUS: 10,
    STATION_POINTS: 50,
    END_BONUS: 200,

    QUEUE_SIZE: 5,

    /** Structured obstacles — placed as blocks */
    OBSTACLE_SHAPES: [
        { w: 2, h: 2, count: 1 },
        { w: 1, h: 2, count: 1 },
        { w: 2, h: 1, count: 1 },
    ],
    SINGLE_BLOCK_CHANCE: 0.02,
    PROTECTED_MARGIN: 3,

    STATION_COUNT_BASE: 4,
    STATION_COUNT_PER_LEVEL: 1,
    STATION_COUNT_CAP: 8,

    COLOR_GRID_BG: 0x1e293b,
    COLOR_GRID_LINE: 0x334155,
    COLOR_TILE_BG: 0x44403c,
    COLOR_START: 0x22c55e,
    COLOR_END: 0xef4444,
    COLOR_STATION: 0x3b82f6,
    COLOR_TRAIN: 0xa855f7,
    COLOR_BLOCKED: 0x0f172a,
};

export const TILE_DEFS = {
    straight: [true, false, true, false],
    curve:    [true, true, false, false],
    cross:    [true, true, true, true],
};

export const TILE_WEIGHTS = { straight: 40, curve: 45, cross: 5 };

export const DIR_DELTA = [
    { dc: 0, dr: -1 },
    { dc: 1, dr: 0 },
    { dc: 0, dr: 1 },
    { dc: -1, dr: 0 },
];

export const OPPOSITE = [2, 3, 0, 1];

export function rotateConnections(base, rotation) {
    const r = ((rotation % 4) + 4) % 4;
    if (r === 0) return [...base];
    const out = new Array(4);
    for (let i = 0; i < 4; i++) out[i] = base[(i - r + 4) % 4];
    return out;
}

export function getExitDir(connections, entryDir) {
    const straight = OPPOSITE[entryDir];
    if (connections[straight]) return straight;
    for (let i = 0; i < 4; i++) {
        if (i !== entryDir && connections[i]) return i;
    }
    return -1;
}

export function drawTileGraphics(g, cs, conn, passed, bgColor) {
    const railColor = passed ? 0x4ade80 : 0xd1d5db;
    const tieColor  = passed ? 0x166534 : 0x78350f;

    const rg = Math.round(cs * 0.14);
    const rw = Math.max(2, Math.round(cs * 0.05));
    const tw = Math.round(cs * 0.09);
    const ts = rg * 2 + rw * 6;
    const c  = cs / 2;

    g.roundRect(1, 1, cs - 2, cs - 2, 4);
    g.fill(bgColor);

    const [n, e, s, w] = conn;
    if (!(n || e || s || w)) return;

    const nsThru = n && s;
    const ewThru = e && w;

    if (nsThru) {
        for (let i = 1; i <= 4; i++) {
            g.rect(c - ts / 2, (cs / 5) * i - tw / 2, ts, tw); g.fill(tieColor);
        }
        g.rect(c - rg - rw / 2, 0, rw, cs); g.fill(railColor);
        g.rect(c + rg - rw / 2, 0, rw, cs); g.fill(railColor);
    }

    if (ewThru) {
        for (let i = 1; i <= 4; i++) {
            g.rect((cs / 5) * i - tw / 2, c - ts / 2, tw, ts); g.fill(tieColor);
        }
        g.rect(0, c - rg - rw / 2, cs, rw); g.fill(railColor);
        g.rect(0, c + rg - rw / 2, cs, rw); g.fill(railColor);
    }

    if (!nsThru && n) {
        g.rect(c - ts / 2, c * 0.45 - tw / 2, ts, tw); g.fill(tieColor);
        g.rect(c - rg - rw / 2, 0, rw, c); g.fill(railColor);
        g.rect(c + rg - rw / 2, 0, rw, c); g.fill(railColor);
    }
    if (!nsThru && s) {
        g.rect(c - ts / 2, c + c * 0.55 - tw / 2, ts, tw); g.fill(tieColor);
        g.rect(c - rg - rw / 2, c, rw, c); g.fill(railColor);
        g.rect(c + rg - rw / 2, c, rw, c); g.fill(railColor);
    }
    if (!ewThru && e) {
        g.rect(c + c * 0.55 - tw / 2, c - ts / 2, tw, ts); g.fill(tieColor);
        g.rect(c, c - rg - rw / 2, c, rw); g.fill(railColor);
        g.rect(c, c + rg - rw / 2, c, rw); g.fill(railColor);
    }
    if (!ewThru && w) {
        g.rect(c * 0.45 - tw / 2, c - ts / 2, tw, ts); g.fill(tieColor);
        g.rect(0, c - rg - rw / 2, c, rw); g.fill(railColor);
        g.rect(0, c + rg - rw / 2, c, rw); g.fill(railColor);
    }

    if (!nsThru && !ewThru) {
        g.circle(c, c, rg + rw * 2); g.fill(tieColor);
    }
}

/**
 * Fixed grid for the 1920x1080 design canvas.
 * Math:  availW = 1920 - 40 = 1880  →  26 cols × 72 px
 *        availH = 1080 - 120 = 960  →  13 rows × 72 px
 *        originX = (1920 - 1872) / 2 = 24
 *        originY = 100 + (960 - 936) / 2 = 112
 */
export const PIPELINE_GRID = Object.freeze({
    cols: 26,
    rows: 13,
    cellSize: 72,
    originX: 24,
    originY: 112,
});

/** Returns the fixed grid. Signature preserved for call-site compatibility. */
export function computeGrid() {
    return { ...PIPELINE_GRID };
}
